/**
 * The per-run parameters (sheet 11 callout 4; FR-006; GEN.3; research §1.16;
 * post-plan S12; spec amendment to US3 scenario 6).
 *
 * No endpoint serves the per-run defaults, so the three fields render empty
 * and each describes the server's default (register 15). Only filled fields are
 * sent: an empty field is omitted from the start body, never sent as "" or
 * null. The cycle identifier is upper-cased as typed. Every input turns
 * spellcheck and autofill off (S12).
 */
import { useId } from 'react'
import { AlertCircle } from 'lucide-react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { copyText, type TextKey } from '@/lib/provisional-copy'

export interface PerRunValue {
  /** YYYY-MM-DD, or '' for the server default. */
  activity_date: string
  date_created: string
  /** Two characters A-Z0-9, or ''. */
  cycle_identifier: string
}

export type PerRunField = keyof PerRunValue
export type PerRunErrors = Partial<Record<PerRunField, string>>
export const PER_RUN_FIELDS: readonly PerRunField[] = ['activity_date', 'date_created', 'cycle_identifier']

export const EMPTY_PER_RUN: PerRunValue = { activity_date: '', date_created: '', cycle_identifier: '' }

/** The filled fields only (C-006): an empty field is the server's default. */
export function filledPerRun(value: PerRunValue): Partial<PerRunValue> {
  const out: Partial<PerRunValue> = {}
  for (const field of PER_RUN_FIELDS) if (value[field] !== '') out[field] = value[field]
  return out
}

const FIELDS: ReadonlyArray<{ field: PerRunField; label: string; type: 'date' | 'text'; help: TextKey }> = [
  { field: 'activity_date', label: 'Activity date', type: 'date', help: 'perRunDefault.activityDate' },
  { field: 'date_created', label: 'Date created', type: 'date', help: 'perRunDefault.dateCreated' },
  { field: 'cycle_identifier', label: 'Cycle identifier', type: 'text', help: 'perRunDefault.cycleIdentifier' },
]

export interface PerRunParametersProps {
  value: PerRunValue
  onChange: (value: PerRunValue) => void
  errors: PerRunErrors
}

export function PerRunParameters({ value, onChange, errors }: PerRunParametersProps) {
  const base = useId()
  return (
    <div className="grid grid-cols-2 gap-4">
      {FIELDS.map(({ field, label, type, help }) => {
        const id = `${base}-${field}`
        const helpId = `${id}-help`
        const errorId = `${id}-error`
        const error = errors[field]
        return (
          <div key={field} className="flex flex-col gap-1.5">
            <Label htmlFor={id}>{label}</Label>
            <Input
              id={id}
              type={type}
              value={value[field]}
              spellCheck={false}
              autoComplete="off"
              {...(field === 'cycle_identifier' ? { maxLength: 2, className: 'font-mono' } : {})}
              aria-invalid={error !== undefined ? true : undefined}
              aria-describedby={error !== undefined ? `${helpId} ${errorId}` : helpId}
              onChange={(event) => {
                const next = field === 'cycle_identifier' ? event.target.value.toUpperCase() : event.target.value
                onChange({ ...value, [field]: next })
              }}
            />
            <p id={helpId} className="text-caption text-navy-600">
              {copyText(help)}
            </p>
            {error !== undefined ? (
              <p id={errorId} className="flex items-center gap-1 text-caption text-[var(--color-error-700)]">
                <AlertCircle aria-hidden className="size-4 shrink-0" />
                {error}
              </p>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
